import { useEffect, useState } from 'react';
import { Badge, Button, Caption1 } from '@fluentui/react-components';
import type { Person } from '@vigeo/shared';
import { AttributeEditor } from '../AttributeEditor.js';
import { answerOf, attributeQuestions } from '../attributes.js';
import { usePersonStyles } from './styles.js';

type AttributeKey = keyof typeof attributeQuestions;

/**
 * Trigger inputs start collapsed (checkpoint 7A): the summary says what is still unanswered,
 * because an unanswered question is what holds a person at "Needs review". The editor opens
 * on demand, or when the page is opened at #trigger-inputs from an "Answer" link.
 */
export function TriggerInputsCard({ person, canEdit, openForLink }: { readonly person: Person; readonly canEdit: boolean; readonly openForLink: boolean }) {
  const styles = usePersonStyles();
  const [open, setOpen] = useState(openForLink);
  useEffect(() => {
    if (!openForLink) return;
    setOpen(true);
    const timer = window.setTimeout(() => document.getElementById('trigger-inputs')?.scrollIntoView?.({ block: 'start' }), 0);
    return () => window.clearTimeout(timer);
  }, [openForLink]);
  useEffect(() => {
    const onHash = () => {
      if (window.location.hash === '#trigger-inputs') setOpen(true);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  const keys = Object.keys(attributeQuestions) as AttributeKey[];
  const unanswered = keys.filter((key) => answerOf(person.attributes[key]) === 'unanswered');
  const answered = keys.length - unanswered.length;

  return (
    <section id="trigger-inputs" className={styles.card} aria-labelledby="trigger-inputs-heading">
      <div className={styles.cardHead}>
        <h2 id="trigger-inputs-heading" className={styles.h}>
          Trigger inputs
        </h2>
        <Button appearance={open ? 'subtle' : 'secondary'} aria-expanded={open} aria-controls="trigger-inputs-editor" onClick={() => setOpen((v) => !v)}>
          {open ? 'Hide answers' : canEdit ? (unanswered.length > 0 ? 'Answer' : 'Edit answers') : 'Show answers'}
        </Button>
      </div>
      <Caption1>
        {answered} answered · {unanswered.length} unanswered
        {unanswered.length > 0 ? '. Unanswered questions hold this person at “Needs review”.' : '.'}
      </Caption1>
      {unanswered.length > 0 ? (
        <div className={styles.chips} aria-label="Unanswered trigger questions">
          {unanswered.map((key) => (
            <Badge key={key} appearance="outline" size="medium" shape="rounded">
              {attributeQuestions[key].question}
            </Badge>
          ))}
        </div>
      ) : null}
      <div id="trigger-inputs-editor" hidden={!open}>
        {open ? <AttributeEditor person={person} canEdit={canEdit} /> : null}
      </div>
    </section>
  );
}
